import React, { Component } from 'react'
import PropTypes from 'prop-types'
import 'intersection-observer' // required polyfill
import Observer from '@researchgate/react-intersection-observer'
import classnames from 'classnames'
import { camelize } from 'humps'

import LoadingIndicator from 'components/LoadingIndicator'
import ProductBlock from 'components/ProductBlock'
import FilterPanel from 'components/FilterPanel'
import Select from 'components/Select'

import styles from './wineList.css'
import {
  TASTE_PROFILE_FILTER
} from 'constants/filtering'
import { trackTasteProfileFilterEvent } from 'utils/tagManager'

const NBSP = '\u00A0'

export class WineList extends Component {
  constructor (props) {
    super(props)
    this.handleLoadMoreIntersect = this.handleLoadMoreIntersect.bind(this)
    this.handleClickClearFilters = this.handleClickClearFilters.bind(this)
    this.handleChangeSelectFilter = this.handleChangeSelectFilter.bind(this)
  }

  componentWillMount () {
    if (!this.props.tasteProfile) {
      // force reinitialisation of the app, specifically for Redux initial state
      this.navigateTo(process.env.WEBSHOP_SMAAKPROFIELEN_PATH + '/doe-de-test/')
    }
  }

  renderProductGrid () {
    const { data, loadMore } = this.props
    const { products, loading } = data
    const hasProducts = products && products.edges.length
    const hasMoreProducts = hasProducts && products.edges.length < products.totalCount
    return (
      <div className={styles.productGridContainer}>
        <div className={styles.productGrid}>
          {hasProducts &&
          products.edges.map((node) => node.node).map((props) => (
            <div key={props.sku} className={styles.productBlockContainer}>
              <ProductBlock {...props} />
            </div>
          ))}
        </div>
        {loading && <LoadingIndicator />}
        {hasMoreProducts &&
        <div className={styles.loadMoreContainer}>
          {!loading && <Observer onChange={this.handleLoadMoreIntersect}>
            <button
              className={styles.loadMoreButton}
              onClick={loadMore}
            >
              Toon meer wijnen
            </button>
          </Observer>
          }
        </div>
        }
      </div>
    )
  }

  renderOrderBy () {
    const { orderings, tasteProfiles } = this.props
    const props = {
      options: orderings.options,
      name,
      label: 'Sorteer op',
      onChange: (event) => this.handleChangeSelectFilter(event, orderings, tasteProfiles),
      styles: {
        wrapper: styles.topFilter,
        label: styles.topFilterLabel,
        select: styles.topFilterSelect
      }
    }

    return <Select {...props} />
  }

  renderNoProductsMessage () {
    return (
      <div className={styles.noProductsMessage}>
        <p>
          Geen producten gevonden die voldoen aan jouw criteria.
        </p>
        <button
          onClick={this.handleClickClearFilters}
          className={'button-clear-filters'}
          {...trackTasteProfileFilterEvent('Filter', 'Wis filters')}
        >
          Wis filters
        </button>
      </div>
    )
  }

  renderContent () {
    const { data } = this.props
    if (data.loading) {
      return this.renderProductGrid()
    }
    return (data.products && data.products.edges && data.products.edges.length)
      ? this.renderProductGrid()
      : this.renderNoProductsMessage()
  }

  render () {
    const {
      filters,
      orderings,
      handleFilterSelect,
      handleClickToggleExpansion,
      tasteProfiles,
      tasteProfile,
      router,
      data
    } = this.props
    const headerClassName = classnames(
      styles.title,
      {
        [styles[camelize(tasteProfile && tasteProfile.tasteProfile)]]: tasteProfile
      })
    return (
      <div>
        <div className={styles.container}>
          <h1 className={headerClassName}>
            Jouw <mark
              className={styles.titleMark}
            >{tasteProfile && tasteProfile.tasteProfile.replace(/ /g, NBSP)}</mark> wijnen
          </h1>
        </div>
        <div className={styles.container}>
          <div className={styles.topFilters}>
            {this.renderOrderBy()}
          </div>
          <FilterPanel
            filters={filters}
            data={data}
            orderings={orderings}
            tasteProfiles={tasteProfiles}
            router={router}
            onFilterSelect={handleFilterSelect}
            onClickToggleExpansion={handleClickToggleExpansion}
            onChangeSelectFilter={this.handleChangeSelectFilter}
          />
          {this.renderContent()}
        </div>
      </div>
    )
  }

  handleChangeSelectFilter (event, { name }, tasteProfiles) {
    this.props.handleFilterSelect(name, event.target.value, tasteProfiles)
  }

  handleClickClearFilters () {
    this.props.clearAllFilters()
  }

  handleLoadMoreIntersect (event) {
    const { data, loadMore } = this.props
    if (event.isIntersecting && !data.loading) {
      loadMore()
    }
  }

  navigateTo (url) {
    window.location = url
  }
}

const filterShape = PropTypes.shape({
  name: PropTypes.string,
  type: PropTypes.string,
  options: PropTypes.array,
  expanded: PropTypes.bool
})

WineList.propTypes = {
  orderings: filterShape.isRequired,
  filters: PropTypes.arrayOf(filterShape).isRequired,
  data: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    products: PropTypes.shape({
      edges: PropTypes.array
    })
  }),
  handleFilterSelect: PropTypes.func.isRequired,
  priceRange: PropTypes.shape({ // eslint-disable-line react/no-unused-prop-types
    minPrice: PropTypes.number,
    maxPrice: PropTypes.number
  }),
  tasteProfile: PropTypes.shape({
    tasteProfile: PropTypes.string.isRequired,
    tasteType: PropTypes.string.isRequired,
    acidity: PropTypes.number.isRequired,
    complexity: PropTypes.number.isRequired,
    intensity: PropTypes.number.isRequired,
    sweetness: PropTypes.number.isRequired,
    tannin: PropTypes.number.isRequired
  }),
  loadMore: PropTypes.func,
  clearAllFilters: PropTypes.func.isRequired,
  router: PropTypes.shape({
    pathname: PropTypes.string,
    query: PropTypes.shape(),
    location: PropTypes.shape({
      pathname: PropTypes.string,
      query: PropTypes.shape({
        [TASTE_PROFILE_FILTER.GET_PARAM]: PropTypes.oneOf(TASTE_PROFILE_FILTER.OPTIONS)
      })
    }),
    push: PropTypes.func
  }),
  tasteProfiles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleClickToggleExpansion: PropTypes.func.isRequired
}

WineList.defaultProps = {
  tasteProfile: null,
  priceRange: null,
  data: null,
  loadMore: null,
  router: null
}
